import { Injectable } from '@angular/core';
import {
    Headers,
    Http,
    RequestMethod,
    RequestOptionsArgs,
    Response,
    ResponseContentType,
    ResponseOptions,
    ResponseOptionsArgs
} from '@angular/http';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AuthHttp } from 'angular2-jwt';
import { isDefined } from 'ionic-angular/util/util';
import { schema } from 'normalizr';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { isString } from 'util';
import { AppConfig } from '../../../app/app.config';
import { SCHEMA_MAP } from '../../../app/app.config.schema-map';
import { AccountObject } from '../../../vas/business-objects/account-object/account-object.model';
import { ConfigQueries } from '../../../vas/business-objects/config/config.queries';
import { User } from '../../../vas/business-objects/user/user.model';
import * as fromRoot from '../../../vas/store/bootstrap';
import { INormalisedResult } from '../../interfaces/interfaces';
import { isInArray } from '../../util/is-in-array';
import { NormalizationService } from '../normalization/normalization.service';
import { ISchedulable } from '../scheduler/schedulable.interface';
import { RestRequest } from './request';
import { ApiRequestOptions } from './request-options';
import Entity = schema.Entity;

export interface IStatusResponse {
    authenticated: boolean;
    ip: string;
}

@Injectable()
export class VASRestService implements ISchedulable {

    static CLASS = 'VASRestService';

    private static _instance: VASRestService;

    busy$: Subject<boolean> = new Subject();
    error$: Subject<Error> = new Subject();

    requestHeaders: RequestOptionsArgs = {
        'Content-type': 'application/json',
        Accept: 'application/json'
    } as RequestOptionsArgs;

    token: string;

    protected logging: boolean = AppConfig.logRestRequests;
    protected onlineStatusRefreshInterval = undefined;
    protected proxyNotConfiguredNotificationShown: boolean = false;
    protected host: string;
    protected endpoint: string;
    protected _authenticated: boolean = false;
    authenticated$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    protected myIP: string;
    private debug = true;

    static getInstance(): VASRestService {
        return VASRestService._instance;
    }

    constructor(
        protected config: AppConfig,
        protected http: Http,
        protected authHttp: AuthHttp,
        protected normalizationService: NormalizationService,
        protected store: Store<fromRoot.State>,
        protected translateService: TranslateService
    ) {

        VASRestService._instance = this;

        this.store.select(ConfigQueries.getRestHost)
            .subscribe(value => {

                if (!value) {
                    console.warn('no value for HOST is set');
                }

                this.host = value || this.config.REST.HOST;
            });

        this.store.select(ConfigQueries.getRestEndpoint).subscribe(value => {

            if (!value) {
                console.warn('no value for ENDPOINT is set');
            }

            this.endpoint = value || this.config.REST.ENDPOINT;
        });

        this.store.select(ConfigQueries.getAuthToken).subscribe(value => {
            this.token = value;
        });
    }

    // protected get busy(): boolean {
    //     return this._busy;
    // }
    //
    // protected set busy(value) {
    //     this._busy = value;
    //     this.busy$.next(this._busy);
    // }

    // protected get online(): boolean {
    //     return  && this._online;
    // }

    protected get authenticated(): boolean {
        return this._authenticated;
    }

    protected set authenticated(value: boolean) {
        this._authenticated = value;
        this.authenticated$.next(value);
    }

    execute(restQuery: RestRequest): Observable<Response> {
        return restQuery.execute();
    }

    createRequestFromOptions(
        route: string,
        options: ApiRequestOptions = new ApiRequestOptions(),
        method?: string | RequestMethod
    ): RestRequest {

        let request: RestRequest;

        request = options.getAuthenticated() ? new RestRequest(this.authHttp) : new RestRequest(this.http);

        request
            .setDebugRoute(this.debug)
            .setHost(this.host)
            .setEndpoint(this.endpoint)
            .setPath(route)
            .setQueryParams(options.getQuery())
            .setMethod(method || RequestMethod.Get)
            .appendHeaders(options.getHeaders());

        return request;
    }

    /**
     *
     * @param authenticated
     * @param route
     * @param method
     * @param headers
     * @param query
     * @returns {RestRequest}
     */
    createRequest(authenticated: boolean, route?: string, method: string | RequestMethod = RequestMethod.Get, headers = {}, query = {}): RestRequest {

        let request: RestRequest;

        request = authenticated ? new RestRequest(this.authHttp) : new RestRequest(this.http);

        request
            .setDebugRoute(this.debug)
            .setHost(this.host)
            .setEndpoint(this.endpoint)
            .setPath(route)
            .setMethod(method);

        const h = {...this.requestHeaders, ...headers};
        Object.keys(h).forEach(key => {
            request.appendHeader(key, h[key]);
        });

        return request;
    }

    get(route: string, entitySchema: string | Entity, additionalHeaders: any = {}): Observable<INormalisedResult> {
        this.logging && console.log('get(%s)', route);

        const request = this.createRequest(true, route, RequestMethod.Get, additionalHeaders);

        return this.handleResponseGet(
            request.execute(),
            isString(entitySchema) ?
                this.getSchema(entitySchema as string) :
                entitySchema as Entity
        );
    }

    handleResponseGet(source: Observable<Response>, entitySchema: Entity): Observable<INormalisedResult> {
        return source

            .retryWhen(attempts => {
                return attempts
                    .zip(Observable.range(1, 4))
                    .mergeMap(([error, i]) => {

                        // don't retry for these statuses
                        if (isInArray(error.status, [403, 404])) {
                            return Observable.throw(error);
                        }

                        if (i > 3) {
                            return Observable.throw(error);
                        }

                        console.log(`Wait ${i} seconds and retrying`);
                        return Observable.timer(i * 1000);
                    });
            })
            .catch((error: Response) => {

                switch (true) {
                    case error.status === 0 && error.type === 3:
                    case error.status === 500: {
                        return Observable.of(new Response(new ResponseOptions({
                            body: '{}',
                            status: 200
                        } as ResponseOptionsArgs)));
                    }
                    default: {
                        throw error;
                    }
                }
            })
            .map((res: Response) => res.json())
            .map(json => {
                let n;

                if (!json || !Object.keys(json).length) {
                    return {};
                }

                try {
                    n = this.normalize(json, entitySchema);
                } catch (e) {
                    console.error('error normalizing entity', e, json, entitySchema);
                    n = {};
                }

                if (this.logging) {
                    Object.keys(n).forEach(key => {
                        console.log(`> entity '${key}' has ${n[key].ids.length} items`);
                    });
                }
                return n;
            });
    }

    simpleGet(
        route: string,
        options: ApiRequestOptions = new ApiRequestOptions()
    ): Observable<Response> {
        this.logging && console.log(`simpleGet(${route})`);

        return this.createRequest(true)
            .setPath(route)
            .setQueryParams(options.getQuery())
            .setMethod(RequestMethod.Get)
            .appendHeaders(options.getHeaders())
            .execute();
    }

    simpleAnonymousGet(
        route: string,
        options: ApiRequestOptions = new ApiRequestOptions()
    ): Observable<Response> {
        this.logging && console.log('simpleGet(%s)', route);
        return this.createRequest(false)
            .setPath(route)
            .setQueryParams(options.getQuery())
            .setMethod(RequestMethod.Get)
            .appendHeaders(options.getHeaders())
            .execute();
    }

    handleBadResponseGET(): (errors: Observable<any>) => Observable<any> {
        return (errors: Observable<Response>) => {
            return errors.switchMap((badResponse: Response) => {

                switch (true) {

                    case badResponse.status === 500:
                        return Observable.fromPromise(this.presentRetry(badResponse)).switchMap(retry => {
                            if (retry === true) {
                                return Observable.of(true);
                            }

                            if (retry === false) {
                                return Observable.throw(badResponse);
                            }

                            return Observable.of(false);
                        });
                    default: {
                        console.error(badResponse);
                        return Observable.throw(badResponse);
                    }
                }
            });
        };
    }

    getReportContent(accountId: string, reportId: string): Observable<any> {
        return this.authHttp.get(
            this.r(`/accounts/${accountId}/reports/${reportId}/content`),
            {
                // headers: new Headers({
                //     "Content-type": ''
                // }),
                responseType: ResponseContentType.Blob
            }
        ).map(response => response.blob());
    }

    patchUser(id: string, user: User): Observable<Response> {
        return this.patch(`/users/${id}`, this.preparePayload('user', user));
    }

    /**
     *
     * @param route
     * @param data
     * @param additionalHeaders
     * @returns {Observable<Response>}
     * @deprecated use execute
     */
    post(route: string, data: object, additionalHeaders?: object): Observable<Response> {
        this.logging && console.log(`post(${route})`, data);
        return this.authHttp.post(this.r(route), data, this.prepareRequestOptionArgs(additionalHeaders))
            .catch((response: Response) => {
                console.warn(`> error: ${response.status} ${response.statusText}`, response.json(), data);
                return Observable.throw(response);
            });
    }

    postAccount(account: object): Observable<Response> {
        return this.post(`/accounts`, this.preparePayload('account', account));
    }

    postAccountObject(entity: string, accountId: string, data: object): Observable<Response> {
        return this.post(
            `/accounts/${accountId}/${this.pluralise(entity)}`,
            this.preparePayload(entity, data)
        );
    }

    postAccountObjectSubResource(accountId: string, entity: string, id: string, entitySub: string, data: object): Observable<Response> {
        return this.post(
            `/accounts/${accountId}/${this.pluralise(entity)}/${id}/${this.pluralise(entitySub)}`,
            this.preparePayload(entitySub, data)
        );
    }

    postUnauthenticated(route: string, data: object, additionalHeaders?: object): Observable<Response> {
        this.logging && console.log('postUnauthenticated(%s)', route);
        return this.http.post(
            this.r(route),
            data,
            this.prepareRequestOptionArgs(additionalHeaders)
        );
    }

    patch(route: string, data: any, additionalHeaders?: object): Observable<Response> {
        this.logging && console.log('patch(%s)', route, data);
        return this.authHttp.patch(
            this.r(route),
            data,
            this.prepareRequestOptionArgs(additionalHeaders)
        );
    }

    patchAccount(id: string, account: object): Observable<Response> {
        return this.patch(`/accounts/${id}`, this.preparePayload('account', account));
    }

    patchAccountObject(entity: string, accountId: string, id: string, data: object, version?: number): Observable<Response> {

        let headers = {};

        if (version) {
            headers = {
                'X-Resource-Version': version
            };
        }

        return this.patch(
            `/accounts/${accountId}/${this.pluralise(entity)}/${id}`,
            this.preparePayload(entity, data),
            headers
        );
    }

    patchAccountObjectSubResource(accountId: string, entity: string, id: string, entitySub: string, idSub: string, data: object, version?: number): Observable<Response> {

        let headers = {};

        if (version) {
            headers = {
                'X-Resource-Version': version
            };
        }

        return this.patch(
            `/accounts/${accountId}/${this.pluralise(entity)}/${id}/${this.pluralise(entitySub)}/${idSub}`,
            this.preparePayload(entitySub, data),
            headers
        );
    }

    put(route: string, data?: any, additionalHeaders?: object): Observable<Response> {
        this.logging && console.log('put(%s)', route);
        return this.authHttp.put(
            this.r(route),
            JSON.stringify(data),
            this.prepareRequestOptionArgs(additionalHeaders)
        );
    }

    putAccount(id: string, account: object): Observable<Response> {
        return this.put(`/accounts/${id}`, account);
    }

    delete(route: string, additionalHeaders?: any): Observable<Response> {
        this.logging && console.log('delete(%s)', route);
        return this.authHttp.delete(
            this.r(route),
            this.prepareRequestOptionArgs(additionalHeaders)
        );
    }

    deleteAccount(id: string): Observable<Response> {
        return this.delete(`/accounts/${id}`);
    }

    deleteAccountObject(entity: string, accountId: string, object: AccountObject): Observable<Response> {
        return this.deleteAccountObjectById(entity, accountId, object.id);
    }

    deleteAccountObjectById(entity: string, accountId: string, id: string): Observable<Response> {
        return this.delete(`/accounts/${accountId}/${this.pluralise(entity)}/${id}`);
    }

    deleteAccountObjectByIdSubResource(accountId: string, entity: string, id: string, entitySub: string, idSub: string): Observable<Response> {
        return this.delete(`/accounts/${accountId}/${this.pluralise(entity)}/${id}/${this.pluralise(entitySub)}/${idSub}`);
    }

    head(route: string, additionalHeaders?: any): Observable<Headers> {
        this.logging && console.log('head(%s)', route);
        return this.authHttp.head(
            this.r(route),
            this.prepareRequestOptionArgs(additionalHeaders)
        )
            .map((res: Response) => res.headers);
    }

    getSchema(entity: string): Entity {

        const schemaMap = SCHEMA_MAP;

        if (typeof schemaMap[entity] === 'undefined') {
            throw new Error(`entity name "${entity}" cannot be mapped to a schema. valid objects are: ${Object.keys(schemaMap).join(', ')}`);
        }

        return schemaMap[entity];
    }

    presentRetry(error: Response): Promise<any> {
        return new Promise((resolve, reject) => {
            //     const prompt = this.alertCtrl.create({
            //         title: this.translateService.instant("POPUP_RETRY_TITLE"),
            //         message: this.translateService.instant("POPUP_RETRY_MESSAGE", {
            //             message: error.statusText
            //         }),
            //         buttons: [
            //             {
            //                 text: this.translateService.instant("RETRY"),
            //                 handler: data => resolve(true)
            //             }, {
            //                 text: this.translateService.instant("SKIP"),
            //                 handler: data => resolve(undefined)
            //             }, {
            //                 text: this.translateService.instant("CANCEL"),
            //                 handler: data => resolve(false)
            //             }
            //         ]
            //     });
            //     prompt.present();
        });
    }

    /**
     *
     * @param since
     * @returns {object}
     * @deprecated use setSince on the RestRequest object
     */
    getModifiedSinceHeaders(since: Date): object {

        if (!since) {
            return {};
        }

        return {
            'If-modified-since': since.toUTCString()
        };
    }

    error(message): void {
        this.error$.next(message);
    }

    /**
     * Prepare the route
     * @param route
     * @returns {string}
     * @deprecated use RestRequest
     */
    r(route): string {

        const rOrig = route;

        let r = route.replace(/^\//, '').replace(/\/$/, '');

        [this.host, this.endpoint].forEach(s => {
            r = r.replace(s, '');
        });

        r = r.replace(/^\//, '').replace(/\/$/, '');

        r = r.toLowerCase();

        r = `${this.host}${this.endpoint}/${r}`;

        if (this.debug) {

            if (r.match(/\?/)) {
                r += '&XDEBUG_SESSION_START=PHPSTORM';
            } else {
                r += '?XDEBUG_SESSION_START=PHPSTORM';
            }

        }

        return r;
    }

    /**
     * This function wraps the data payload so that it can be accepted
     * by the Symfony Forms handler for POSTs and PATCHes
     * @todo review if we can get rid of this. The wrapper appears to be redundant
     * @param entity
     * @param data
     * @returns {{}}
     */
    preparePayload(entity: string, data: object): {} {
        return {
            [entity]: data
        };
    }

    /**
     *
     * @param entity
     * @param data
     * @returns {any}
     * @deprecated this shouldn't be necessary any more as we are using DTOs
     */
    cleanPayload(entity: string, data): any {
        if (!data) {
            return data;
        }

        const keysNotAllowed = [
            // "modified",
            // "modifiedBy",
            'serverModified',
            'serverModifiedBy',
            'created',
            'createdBy'
            // "serverCreated",
            // "serverCreatedBy",
            // "archived"

        ];

        const dataClean = {};

        Object.keys(data)
            .filter(key => !isInArray(key, keysNotAllowed))
            .forEach(key => dataClean[key] = data[key]);

        return dataClean;
    }

    normalize(data, entitySchema): any {
        return this.normalizationService.normalize(data, entitySchema);
    }

    getLocation(response: Response): string {
        return response.headers.get('Location');
    }

    /**
     *
     * @param value
     * @returns {string}
     * @deprecated use RestRequest
     */
    pluralise(value: string): string {
        return value.substring(value.length - 1) === 's' ?
            `${value}es` :
            `${value}s`;
    }

    /**
     *
     * @param additionalHeaders
     * @returns {{headers: {}}}
     * @deprecated use RestRequest
     */
    prepareRequestOptionArgs(additionalHeaders?): any {
        const headers = {...this.requestHeaders, ...(additionalHeaders || {})};

        const headersClean = {};

        Object.keys(headers).forEach(key => {

            if (!isDefined(headers[key])) return;

            headersClean[key] = headers[key];
        });
        return {
            headers: headersClean
        };
    }

    isAuthenticated(): boolean {
        return this._authenticated;
    }

    // myIp(): string {
    //     return this.myIP;
    // }

    isBusy(): boolean {
        // return this.busy;
        return false;
    }

    getSchedulerId(): string {
        return VASRestService.CLASS;
    }

    run(): Promise<any> {
        return Promise.resolve();
    }

    stop(): void {
        console.log('vas rest stop()');
    }
}



// WEBPACK FOOTER //
// ./src/core/services/api/vas-rest.ts